import { getItem, setItem, type LocalStorageKeys } from 'owa-local-storage';
import { getMailboxKeyForMailboxInfo, type MailboxInfo } from 'owa-client-types';
import { userDate, getDate, getMonth } from 'owa-datetime';

/**
 * Returns a number uniquely identifying today within a year (month * 100 + day-of-month).
 * E.g., Jan 5 = 105, Dec 31 = 1231.
 */
export function getTodayIdentifier(): number {
    const now = userDate();
    return (getMonth(now) + 1) * 100 + getDate(now);
}

interface NeedsReplyDismissPersistedState {
    lastDismissedDay: number | null;
    lastKnownCount?: number;
}

const LOCAL_STORAGE_KEY_PREFIX = 'NeedsReplyDismissState';

function getStorageKey(mailboxInfo: MailboxInfo): LocalStorageKeys {
    return `${LOCAL_STORAGE_KEY_PREFIX}_${getMailboxKeyForMailboxInfo(mailboxInfo)}`;
}

function loadState(mailboxInfo: MailboxInfo): NeedsReplyDismissPersistedState | null {
    try {
        const stored = getItem(self, getStorageKey(mailboxInfo));
        if (!stored) {
            return null;
        }
        return JSON.parse(stored) as NeedsReplyDismissPersistedState;
    } catch {
        return null;
    }
}

function saveState(mailboxInfo: MailboxInfo, state: NeedsReplyDismissPersistedState): void {
    try {
        setItem(self, getStorageKey(mailboxInfo), JSON.stringify(state));
    } catch {
        // Silently fail if localStorage is not available
    }
}

export function loadNeedsReplyDismissState(mailboxInfo: MailboxInfo): number | null {
    return loadState(mailboxInfo)?.lastDismissedDay ?? null;
}

export function saveNeedsReplyDismissState(
    mailboxInfo: MailboxInfo,
    lastDismissedDay: number | null
): void {
    const existing = loadState(mailboxInfo);
    saveState(mailboxInfo, {
        lastDismissedDay,
        lastKnownCount: existing?.lastKnownCount,
    });
}

export function getPersistedCount(mailboxInfo: MailboxInfo): number | null {
    return loadState(mailboxInfo)?.lastKnownCount ?? null;
}

export function savePersistedCount(mailboxInfo: MailboxInfo, count: number): void {
    const existing = loadState(mailboxInfo);
    saveState(mailboxInfo, {
        lastDismissedDay: existing?.lastDismissedDay ?? null,
        lastKnownCount: count,
    });
}

export function isDismissedToday(mailboxInfo: MailboxInfo): boolean {
    const lastDismissedDay = loadNeedsReplyDismissState(mailboxInfo);
    if (lastDismissedDay === null) {
        return false;
    }
    return lastDismissedDay === getTodayIdentifier();
}
